import React,{useEffect,useRef,useState} from 'react';
import {AnimatePresence,motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import {useSupabaseAuth} from '../../contexts/SupabaseAuthContext';
import {useSupabaseData} from '../../contexts/SupabaseDataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  NOTIFICATION_STORAGE_KEY,
  sendLocalNotification
} from '../../services/notificationService';

const {FiArrowRight,FiCheckCircle,FiX}=FiIcons;
const SHOWN_NOTIFICATIONS_KEY='sohlocal_shown_pickup_notifications';

const getShownNotificationIds=(userId)=>{
  if (!userId || typeof window==='undefined') return new Set();

  try {
    const stored=window.localStorage.getItem(
      `${SHOWN_NOTIFICATIONS_KEY}:${userId}`
    );
    return new Set(stored ? JSON.parse(stored) : []);
  } catch {
    return new Set();
  }
};

const saveShownNotificationIds=(userId,ids)=>{
  if (!userId || typeof window==='undefined') return;

  try {
    window.localStorage.setItem(
      `${SHOWN_NOTIFICATIONS_KEY}:${userId}`,
      JSON.stringify(Array.from(ids).slice(-100))
    );
  } catch {
    // Local storage may be unavailable in private browsing.
  }
};

const PickupNotificationToast=()=>{
  const {user}=useSupabaseAuth();
  const {pickupNotifications,markPickupNotificationRead}=useSupabaseData();
  const [visibleNotification,setVisibleNotification]=useState(null);
  const initializedUserRef=useRef(null);
  const knownNotificationIdsRef=useRef(new Set());
  const shownNotificationIdsRef=useRef(new Set());

  useEffect(()=>{
    if (!user) {
      initializedUserRef.current=null;
      knownNotificationIdsRef.current=new Set();
      shownNotificationIdsRef.current=new Set();
      setVisibleNotification(null);
      return;
    }

    if (initializedUserRef.current!==user.id) {
      initializedUserRef.current=user.id;
      knownNotificationIdsRef.current=new Set();
      shownNotificationIdsRef.current=getShownNotificationIds(user.id);
      setVisibleNotification(null);
    }
  },[user]);

  useEffect(()=>{
    if (
      !user ||
      !Array.isArray(pickupNotifications) ||
      initializedUserRef.current!==user.id
    ) {
      return;
    }

    const notifications=pickupNotifications.filter(
      (notification)=>(
        notification.user_id===user.id &&
        [
          'pickup_ready',
          'pickup_completed',
          'order_ready',
          'order_completed'
        ].includes(
          notification.notification_type
        )
      )
    );

    const isFirstLoad=knownNotificationIdsRef.current.size===0;

    if (isFirstLoad) {
      notifications.forEach((notification)=>{
        knownNotificationIdsRef.current.add(notification.id);
      });
      return;
    }

    const newNotification=notifications.find(
      (notification)=>(
        !knownNotificationIdsRef.current.has(notification.id) &&
        !shownNotificationIdsRef.current.has(notification.id)
      )
    );

    notifications.forEach((notification)=>{
      knownNotificationIdsRef.current.add(notification.id);
    });

    if (!newNotification) return;

    shownNotificationIdsRef.current.add(newNotification.id);
    saveShownNotificationIds(
      user.id,
      shownNotificationIdsRef.current
    );

    if (
      typeof window!=='undefined' &&
      window.localStorage.getItem(NOTIFICATION_STORAGE_KEY)==='true'
    ) {
      sendLocalNotification(
        newNotification.title,
        {
          body:newNotification.body,
          tag:`sohlocal-order-${newNotification.order_id || newNotification.id}`,
          data:{
            orderUrl:newNotification.metadata?.order_url
          }
        }
      );
    }

    setVisibleNotification(newNotification);
  },[pickupNotifications,user]);

  useEffect(()=>{
    if (!visibleNotification) return undefined;

    const timer=window.setTimeout(()=>{
      markPickupNotificationRead(visibleNotification.id).catch(
        (error)=>console.error(
          'Unable to mark pickup notification read:',
          error
        )
      );
      setVisibleNotification(null);
    },6000);

    return ()=>window.clearTimeout(timer);
  },[visibleNotification,markPickupNotificationRead]);

  const dismiss=()=>{
    if (!visibleNotification) return;

    markPickupNotificationRead(visibleNotification.id).catch(
      (error)=>console.error(
        'Unable to mark pickup notification read:',
        error
      )
    );
    setVisibleNotification(null);
  };

  if (!visibleNotification) return null;

  const orderNumber=String(
    visibleNotification.metadata?.order_number ||
    visibleNotification.order_id?.slice(0,8) ||
    ''
  ).toUpperCase();

  const isCompleted=
    visibleNotification.notification_type==='pickup_completed' ||
    visibleNotification.notification_type==='order_completed';

  const isReady=
    visibleNotification.notification_type==='pickup_ready' ||
    visibleNotification.notification_type==='order_ready';

  const notificationBody=
    visibleNotification.notification_type==='order_completed' ||
    visibleNotification.notification_type==='order_ready'
      ? visibleNotification.body
      : isCompleted
        ? 'The order has been verified and marked completed.'
        : 'Your order is ready for pickup. Open your order to view the pickup QR code or code.';

  return (
    <AnimatePresence>
      <motion.div
        initial={{opacity:0,y:-20,scale:0.96}}
        animate={{opacity:1,y:0,scale:1}}
        exit={{opacity:0,y:-20,scale:0.96}}
        className="fixed right-4 top-28 z-[110] w-[calc(100%-2rem)] max-w-sm rounded-2xl border border-indigo-100 bg-white p-4 shadow-2xl shadow-indigo-900/10"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <SafeIcon
            icon={FiCheckCircle}
            className="mt-0.5 flex-shrink-0 text-2xl text-indigo-600"
          />

          <div className="min-w-0 flex-1">
            <p className="text-sm font-black text-slate-900">
              {isCompleted
                ? `Order #${orderNumber} completed`
                : isReady
                  ? `Order #${orderNumber} ready for pickup`
                  : `Order #${orderNumber}`}
            </p>

            <p className="mt-1 text-xs font-medium leading-relaxed text-slate-500">
              {notificationBody}
            </p>

            {visibleNotification.order_id && (
              <Link
                to={`/orders?order=${visibleNotification.order_id}`}
                onClick={dismiss}
                className="mt-3 inline-flex items-center text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-800"
              >
                View order #{orderNumber}
                <SafeIcon icon={FiArrowRight} className="ml-2" />
              </Link>
            )}
          </div>

          <button
            type="button"
            onClick={dismiss}
            className="rounded-lg p-1 text-slate-300 transition-colors hover:bg-slate-50 hover:text-slate-700"
            aria-label="Dismiss notification"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PickupNotificationToast;